import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import SectionCard from '../../../components/ui/SectionCard'
import StackedBar from '../../../components/ui/StackedBar'
import { STATUS_TONE_COLOR_VAR } from '../../actions/types'
import type { RemainingBucketKey, RemainingTimeBucket } from '../types'

interface Props {
  buckets: RemainingTimeBucket[]
  /** Sağ üstteki "Tümünü Gör" bağlantısı (Raporum revizyonu §4.4). */
  hint?: ReactNode
  /** Seçili aralık; `null` → seçim yok. Yalnız {@link Props.onSelect} ile birlikte anlamlıdır. */
  selectedBucket?: RemainingBucketKey | null
  /**
   * Bir bara tıklandı (Raporum revizyonu §4.4: "Barlara tıklandığında Aksiyon Beklenen Maddeler
   * tablosunda ilgili aksiyonlar gösterilecektir"). Aynı bara yeniden tıklamanın "seçimi kaldır" olması
   * ÇAĞIRANIN işidir.
   */
  onSelect?: (bucket: RemainingBucketKey) => void
}

/**
 * "Kalan Süre Dağılımı" (068 — analiz §2.6, spec §K10; 2026-08-19 revizyonu §4): açık ve termini
 * **gelecekte** olan aksiyonların `0–7 / 8–30 / 31–90 / 91+` gün aralıklarına dağılımı; yatay bar + adet
 * + yüzde.
 *
 * <p>Dört aralık **her zaman** görünür (adedi 0 olsa bile): bu bir dilim listesi değil, sabit bir
 * eksendir — "8–30 gün aralığında hiç işim yok" bilgisi de bir bilgidir ve satırın kaybolması onu gizler.
 *
 * <p>Termini geçmiş aksiyonlar bu dağılıma **girmez** (analiz §4.2); onlar "Geciken Aksiyonlar"
 * tablosundadır. Bu yüzden bölümün toplamı panonun toplam aksiyon sayısından küçüktür — beklenen davranış.
 *
 * <p>Barlar ortak ölçekle çizilir (`max` = en kalabalık aralık) → aralıklar birbiriyle karşılaştırılabilir.
 *
 * <p><b>Seçim:</b> `onSelect` verildiğinde satırın tamamı gerçek bir `<button>` olur (bar da etiket de
 * aynı hedefin parçasıdır); `aria-pressed` seçili aralığı renkten bağımsız olarak da bildirir.
 */
export default function RemainingTimeCard({
  buckets,
  hint,
  selectedBucket = null,
  onSelect,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const max = buckets.reduce((acc, b) => Math.max(acc, b.count), 0)
  const selectable = onSelect != null

  return (
    <SectionCard title={t('home.my.remaining.title')} hint={hint} className="home-remaining">
      <ul className="home-remaining-list">
        {buckets.map((b) => {
          const label = t(`home.my.remaining.${b.bucket}`)
          const selected = selectedBucket === b.bucket
          const content = (
            <>
              {/* `title`: dar kartta etiket ellipsis'e düşebilir (bkz. `home.css` → `.home-remaining-list`);
                  tam metin o durumda da erişilebilir kalır — `.home-dist-name` ile aynı desen. */}
              <span className="home-remaining-label" title={label} style={{ fontSize: '12px', fontWeight: 600 }}>
                {label}
              </span>
              <StackedBar
                ariaLabel={`${label}: ${b.count}`}
                max={max}
                className="home-remaining-bar"
                segments={[
                  { key: b.bucket, label, value: b.count, color: STATUS_TONE_COLOR_VAR.ongoing },
                ]}
              />
              <span className="home-remaining-count" style={{ fontSize: '14px' }}>{b.count}</span>
              <span className="home-remaining-pct" style={{ fontSize: '14px' }}>%{b.percent}</span>
            </>
          )
          return (
            <li
              key={b.bucket}
              className={selected ? 'home-remaining-item home-remaining-item--on' : 'home-remaining-item'}
            >
              {selectable ? (
                <button
                  type="button"
                  className="home-remaining-row home-remaining-btn"
                  aria-pressed={selected}
                  onClick={() => onSelect(b.bucket)}
                >
                  {content}
                </button>
              ) : (
                <span className="home-remaining-row">{content}</span>
              )}
            </li>
          )
        })}
      </ul>
    </SectionCard>
  )
}
